import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { LicenseInfo } from "@mui/x-license-pro";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ErrorMessage from "../ErrorMessage";
import useFetch from "../../../hooks/useFetch";
import { getText } from "../../../hooks/useCustomer";
import InternalLink from "../../routing/InternalLink";
import SearchIcon from "@mui/icons-material/Search";
import FullPageGridContainer from "../../layout/FullPageGridContainer";
import Header from "../../layout/Header";
import { Tooltip } from "@mui/material";
import InvoiceAdvancedSearch from "./InvoiceAdvancedSearch";
import { Box } from "@mui/material";
import usePermissions from "../../../hooks/usePermissions";

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY);

const renderCell = (params) => (
  <Tooltip title={params.value || ""}>
    <span>{params.formattedValue}</span>
  </Tooltip>
);

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const usdPrice = {
  type: "number",
  valueFormatter: ({ value }) => currencyFormatter.format(Number(value)),
};

const InvoiceList = (props) => {
  const { salesOrderId, customerId } = useParams();
  const params = Object.fromEntries(
    new URLSearchParams(window.location.search)
  );
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [invoices, setInvoices] = useState([]);
  const [salesOrder, setSalesOrder] = useState({});
  const [customer, setCustomer] = useState({});
  const columns = [
    {
      field: "view",
      headerName: "View",
      width: 18,
      resizable: false,
      renderCell: (params) => (
        <InternalLink to={`/invoices/${params?.row?.id}`}>
          <SearchIcon />
        </InternalLink>
      ),
      renderHeader: () => <SearchIcon className="text-gray-400" />,
    },
    { field: "id", headerName: "ID", hide: true },
    { field: "trandate", headerName: "Date", description: "", maxWidth: 98 },
    { field: "tranid", headerName: "INV #", description: "", maxWidth: 100 },
    {
      renderCell,
      field: "otherrefnum",
      headerName: "PO #",
      description: "",
      minWidth: 160,
      flex: 1,
    },
    {
      renderCell,
      field: "customer",
      headerName: "Customer",
      description: "",
      minWidth: 190,
      flex: 1,
    },
    {
      renderCell,
      field: "shipaddressee",
      headerName: "Ship To",
      description: "",
      minWidth: 160,
      flex: 1,
    },
    { field: "status", headerName: "Status", description: "", width: 150 },
    {
      renderCell,
      field: "salesrep",
      headerName: "Inside Sales Rep",
      description: "Inside sales rep in Netsuite",
      minWidth: 120,
      flex: 1,
      hide: true,
    },
    {
      renderCell,
      field: "costcorep",
      headerName: "Costco Sales Rep",
      description: "Costco sales rep in Netsuite",
      minWidth: 120,
      flex: 1,
      hide: true,
    },
    {
      renderCell,
      field: "partner",
      headerName: "Outside Sales Rep",
      description: "Outside sales rep in Netsuite",
      minWidth: 120,
      flex: 1,
      hide: true,
    },
    {
      field: "amount",
      headerName: "Amount",
      description: "",
      width: 150,
      ...usdPrice,
    },
    {
      field: "formulanumeric",
      headerName: "Days Overdue",
      description: "",
      width: 150,
      hide: true,
      type: "number",
    },
  ];
  const netsuiteColumns = [
    "internalid",
    { name: "trandate", sort: "DESC" },
    "otherrefnum",
    "tranid",
    "shipaddressee",
    "status",
    "entity",
    "amount",
    "salesrep",
    "custbody_costco_sales_rep",
    "partner",
    {
      name: "formulanumeric",
      formula: "FLOOR(ABS(COALESCE({duedate},{trandate}) - {today}))",
    },
  ];

  // Add ISR Supervisor
  if (permissions.canViewAllInvoices) {
    columns.push({
      renderCell,
      field: "isr_supervisor",
      headerName: "ISR Supervisor",
      description: "Inside sales rep's supervisor in Netsuite",
      minWidth: 120,
      flex: 1,
      hide: true,
    });
    netsuiteColumns.push("salesrep.supervisor");
  }

  // Set filters for netsuite api
  const netsuiteFilters = [];
  if (params.salesrep)
    netsuiteFilters.push("AND", ["salesrep", "is", params.salesrep]);
  if (params.supervisor)
    netsuiteFilters.push("AND", [
      "salesrep.supervisor",
      "is",
      params.supervisor,
    ]);
  if (params.costcorep)
    netsuiteFilters.push("AND", [
      "custbody_costco_sales_rep",
      "is",
      params.costcorep,
    ]);
  if (params.partner)
    netsuiteFilters.push("AND", ["partner", "is", params.partner]);
  if (params.customer)
    netsuiteFilters.push("AND", [
      "customer.altname",
      "contains",
      params.customer,
    ]);
  if (params.shipaddressee)
    netsuiteFilters.push("AND", [
      "shipaddressee",
      "contains",
      params.shipaddressee,
    ]);
  if (params.status)
    netsuiteFilters.push("AND", ["status", "is", `CustInvc:${params.status}`]);
  if (params.startdate && params.enddate)
    netsuiteFilters.push("AND", [
      "trandate",
      "within",
      params.startdate,
      params.enddate,
    ]);
  if (params.shipcountry)
    netsuiteFilters.push("AND", ["shipcountry", "is", params.shipcountry]);
  if (params.shipstate)
    netsuiteFilters.push("AND", ["shipstate", "is", params.shipstate]);
  if (params.numbertext)
    netsuiteFilters.push("AND", ["numbertext", "contains", params.numbertext]);
  if (params.poastext)
    netsuiteFilters.push("AND", ["poastext", "contains", params.poastext]);
  if (salesOrderId)
    netsuiteFilters.push("AND", ["createdfrom", "is", salesOrderId]);
  if (customerId) netsuiteFilters.push("AND", ["entity", "is", customerId]);
  if (params.overduebalance === "true") {
    netsuiteFilters.push(
      "AND",
      ["formuladate: COALESCE({duedate},{trandate})", "before", "today"],
      "AND",
      ["status", "is", "CustInvc:A"]
    );
    // Find index of the overdue balance column
    const overdueBalanceIndex = columns.findIndex(
      (column) => column.field === "formulanumeric"
    );

    // Unhide the column
    columns[overdueBalanceIndex].hide = false;
  }

  // Set the url
  // Build the URL safely (percent-encode the JSON)
  const base = `${process.env.REACT_APP_BASE_URL}/api/invoices`;
  const qs = new URLSearchParams();

  qs.set("results", JSON.stringify(netsuiteColumns));
  if (netsuiteFilters.length) qs.set("params", JSON.stringify(netsuiteFilters));
  if (salesOrderId) qs.set("salesOrderId", String(salesOrderId));
  if (customerId) qs.set("customerId", String(customerId));

  const url = `${base}?${qs.toString()}`;

  // Fetch
  const { error, isLoading, data: json } = useFetch({ url });

  useEffect(() => {
    if (json) {
      const {
        salesOrder: salesOrderData = {},
        customer: customerData = {},
        invoices: { result, data } = {},
      } = json;
      console.log(json);

      if (result === "success") {
        setInvoices(
          data.map(
            ({
              internalid: [{ value: id } = {}] = [],
              entity: [{ text: customer } = {}] = [],
              status: [{ text: status } = {}] = [],
              salesrep: [{ text: salesrep } = {}] = [],
              "salesrep.supervisor": [{ text: isr_supervisor } = {}] = [],
              custbody_costco_sales_rep: [{ text: costcorep } = {}] = [],
              partner: [{ text: partner } = {}] = [],
              amount,
              otherrefnum,
              trandate,
              tranid,
              shipaddressee,
              formulanumeric,
            }) => ({
              id,
              customer,
              status,
              amount,
              salesrep,
              isr_supervisor,
              costcorep,
              partner,
              otherrefnum,
              trandate,
              tranid,
              shipaddressee,
              formulanumeric,
            })
          )
        );
      }

      if (salesOrderData.result === "success") {
        setSalesOrder({
          id: getText(salesOrderData.data[0], "internalid"),
          tranid: getText(salesOrderData.data[0], "tranid"),
        });
      }

      if (customerData.result === "success") {
        setCustomer({
          id: getText(customerData.data[0], "internalid"),
          name: getText(customerData.data[0], "altname"),
        });
      }
    }
  }, [json]);

  // Set header
  let header = "Invoices";
  if (params.overduebalance === "true" && customerId && !isLoading) {
    header = `Overdue Invoices for ${customer.name}`;
  } else if (customerId && !isLoading) {
    header = `Invoices for ${customer.name}`;
  } else if (salesOrderId && !isLoading) {
    header = `Invoices for ${salesOrder.tranid}`;
  }

  window.document.title = `${header} | Suite Sales`;

  if (error) return <ErrorMessage error={error} />;

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer className="flex justify-between">
        <Box>
          <GridToolbarColumnsButton />
          <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
        </Box>
        {!salesOrderId && !customerId && <InvoiceAdvancedSearch />}
      </GridToolbarContainer>
    );
  };

  return (
    <>
      <Header header={header} className="justify-between"></Header>
      <FullPageGridContainer>
        <DataGridPro
          rows={invoices}
          disableSelectionOnClick={true}
          onRowDoubleClick={(e) => navigate(`/invoices/${e.id}`)}
          columns={columns}
          // autoHeight={true}
          loading={isLoading}
          density="standard"
          disableColumnMenu={true}
          components={{
            Toolbar: CustomToolbar,
          }}
        />
      </FullPageGridContainer>
    </>
  );
};

export default InvoiceList;
